import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/321abe17-07ae-48d4-bb77-6255964d0d96/images/c75fb099219351b6f746e3f9a95819422656e82151b26f612d13b75b0bd6b75a.png";

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Bonjour ! Je suis l\'assistant OZCBNB. Comment puis-je vous aider aujourd\'hui ? Vous avez des questions sur notre plateforme de gestion locative ?'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showPulse, setShowPulse] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Hide pulse after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await api.post('/chatbot/public', {
        message: userMessage,
        session_id: sessionId
      });

      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, je rencontre un problème technique. Vous pouvez nous contacter directement à ozcbnbcontact@gmail.com' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    "C'est quoi OZCBNB ?",
    "Quel est le tarif ?",
    "Comment ça fonctionne ?"
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
    setTimeout(() => {
      const form = document.getElementById('chatbot-form');
      if (form) form.requestSubmit();
    }, 100);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => { setIsOpen(true); setShowPulse(false); }}
        className={`fixed bottom-6 right-6 z-50 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 ${isOpen ? 'hidden' : 'flex'} items-center justify-center group`}
        data-testid="chatbot-toggle"
        aria-label="Ouvrir le chat"
      >
        <MessageCircle className="w-6 h-6" />
        {showPulse && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
          </span>
        )}
        <span className="absolute right-full mr-3 bg-white text-[#0A0A0A] px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          Une question ?
        </span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-50 w-[380px] h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-[#E5E5E5] animate-scale-in"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-[#0A0A0A] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <img src={LOGO_URL} alt="OZCBNB" className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Assistant OZCBNB</h3>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span className="text-xs text-gray-300">En ligne</span>
                </div>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)} 
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Fermer le chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAFAFA]">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-[#0A0A0A]' : 'bg-white border border-[#E5E5E5]'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-[#0A0A0A]" />
                    )}
                  </div>
                  <div className={`px-4 py-3 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-[#0A0A0A] text-white rounded-tr-md' 
                      : 'bg-white text-[#333] border border-[#E5E5E5] rounded-tl-md'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[#0A0A0A]" />
                  </div>
                  <div className="bg-white border border-[#E5E5E5] px-4 py-3 rounded-2xl rounded-tl-md">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#666]" />
                      <span className="text-sm text-[#666]">Réflexion en cours...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 border-t border-[#E5E5E5] bg-white">
              <p className="text-xs text-[#999] mb-2">Questions fréquentes :</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((question, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickQuestion(question)}
                    className="text-xs bg-[#F5F5F5] hover:bg-[#E5E5E5] text-[#333] px-3 py-1.5 rounded-full transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <form 
            id="chatbot-form"
            onSubmit={handleSubmit} 
            className="p-4 border-t border-[#E5E5E5] bg-white"
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 px-4 py-2.5 bg-[#F5F5F5] rounded-xl border-0 focus:ring-2 focus:ring-[#0A0A0A] text-sm"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <Button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white p-2.5 rounded-xl disabled:opacity-50"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-[#999] mt-2 text-center">
              Propulsé par IA - Réponses générées automatiquement
            </p>
          </form>
        </div>
      )}
    </>
  );
}
